// Public warranty lookup — /bao-hanh (no auth required)
// Uses raw axios to avoid sending auth bearer token.
import { useState } from "react";
import axios from "axios";
import { ShieldCheck, ShieldOff, Search } from "lucide-react";

const API_BASE = import.meta.env.VITE_API_URL ?? "http://localhost:8080";

interface PublicSerialInfo {
  productName: string;
  variantName: string;
  sku: string;
  serialNo: string;
  purchasedAt?: string;
  warrantyStart?: string;
  warrantyEnd?: string;
  daysRemaining: number;
  isValid: boolean;
  totalClaims: number;
  status: string;
}

function fmt(d?: string) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("vi-VN");
}

export function PublicLookupPage() {
  const [serial, setSerial] = useState("");
  const [info, setInfo] = useState<PublicSerialInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleLookup(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = serial.trim();
    if (!trimmed) return;

    setLoading(true);
    setError(null);
    setInfo(null);

    try {
      const res = await axios.get<{ data: PublicSerialInfo }>(
        `${API_BASE}/public/warranty/${encodeURIComponent(trimmed)}`
      );
      setInfo(res.data.data);
    } catch (err: unknown) {
      const e = err as { response?: { status?: number; data?: { error?: { message?: string } } } };
      if (e.response?.status === 429) {
        setError("Bạn tra cứu quá nhiều, thử lại sau 1 phút.");
      } else if (e.response?.status === 404) {
        setError("Không tìm thấy serial này trong hệ thống.");
      } else {
        const detail = e.response?.data?.error?.message;
        setError(detail && /[à-ỹĐđ]/i.test(detail) ? detail : "Có lỗi xảy ra, vui lòng thử lại.");
      }
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-background flex flex-col items-center py-16 px-4">
      <div className="w-full max-w-lg space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold">Tra cứu bảo hành</h1>
          <p className="text-muted-foreground text-sm">
            Nhập số serial để kiểm tra thông tin bảo hành sản phẩm
          </p>
        </div>

        {/* Search form */}
        <form onSubmit={handleLookup} className="flex gap-2">
          <input
            type="text"
            placeholder="Nhập serial number..."
            className="flex-1 rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            value={serial}
            onChange={(e) => setSerial(e.target.value)}
            disabled={loading}
            autoFocus
          />
          <button
            type="submit"
            disabled={loading || !serial.trim()}
            className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            <Search size={16} />
            {loading ? "Đang tìm..." : "Tra cứu"}
          </button>
        </form>

        {/* Error */}
        {error && (
          <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {error}
          </div>
        )}

        {/* Result card */}
        {info && (
          <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
            {/* Validity banner */}
            <div className={`flex items-center gap-3 px-6 py-4 ${
              info.isValid ? "bg-green-500/10 text-green-700" : "bg-red-500/10 text-red-700"
            }`}>
              {info.isValid
                ? <ShieldCheck size={28} className="shrink-0" />
                : <ShieldOff size={28} className="shrink-0" />}
              <div>
                <div className="text-xl font-bold">
                  {info.isValid ? "CÒN BẢO HÀNH" : "HẾT BẢO HÀNH"}
                </div>
                {info.isValid && info.daysRemaining > 0 && (
                  <div className="text-sm font-medium">
                    Còn {info.daysRemaining} ngày
                  </div>
                )}
              </div>
            </div>

            {/* Details */}
            <div className="px-6 py-4 space-y-3 text-sm">
              <Row label="Sản phẩm" value={`${info.productName} — ${info.variantName}`} />
              <Row label="SKU" value={info.sku} />
              <Row label="Serial" value={info.serialNo} mono />
              <Row label="Ngày mua" value={fmt(info.purchasedAt)} />
              <Row label="Bắt đầu BH" value={fmt(info.warrantyStart)} />
              <Row label="Hết hạn BH" value={fmt(info.warrantyEnd)} />
              <Row label="Số lần đã BH" value={String(info.totalClaims)} />
            </div>
          </div>
        )}

        <p className="text-center text-xs text-muted-foreground">
          Nhật Minh Store · Hỗ trợ: 1900 xxxx
        </p>
      </div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex justify-between gap-4">
      <span className="text-muted-foreground shrink-0">{label}</span>
      <span className={`text-right font-medium ${mono ? "font-mono" : ""}`}>{value}</span>
    </div>
  );
}
